<template>
  <div
    class="plan-selection-card"
    :class="{
      'plan-selection-card-decline__selected': featuredCard && foundationDeclined,
      'plan-selection-card-foundation__selected': featuredCard && !foundationDeclined
    }"
    @click="handleCardClick(title)"
  >
    <div
      class="plan-selection-card__header"
      :class="headerGradient"
    >
      <img
        v-if="addBanner"
        class="plan-selection-card__banner"
        :class="'plan-selection-card__banner--large'"
        :src="bannerURL"
      >
      <img
        v-if="addBanner && isBloomFlow"
        class="plan-selection-card__badge"
        :src="require('@/assets/svg/foundation/bloom-badge.svg')"
      >
      <div class="plan-selection-card__header--text-container">
        <span class="plan-selection-card__title">{{ title }}</span>
        <span
          class="plan-selection-card__subtitle"
        >
          {{ subtitle }}
        </span>
      </div>
    </div>
    <div
      class="plan-selection-card__feature-list"
      :class="!readOnly && 'plan-selection-card__feature-list--show-banner'"
    >
      <span
        v-if="!readOnly"
        class="plan-selection-card__feature-list--header"
        :class="textColorClass()"
      >
        Your Features
      </span>
      <div
        v-if="showFeatureInfo"
        class="plan-selection-card__feature-display"
      >
        <button
          class="flex mb-10 mt-5"
          @click="showFeatureInfo = false"
        >
          <font-awesome-icon
            v-if="clickedItem.checked"
            class="mt-5"
            :class="textColorClass()"
            icon="check"
          />
          <font-awesome-icon
            v-else
            class="plan-selection-card__xmark mt-5"
            icon="xmark"
          />

          <span class="font-semibold px-20 text-left">
            {{ clickedItem.text }}
          </span>
          <font-awesome-icon
            class="ml-auto mt-5"
            icon="chevron-down"
          />
        </button>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="mt-10 flex-1"
          v-html="clickedItem.answer"
        />
        <img
          v-if="clickedItem.bloomOnly"
          :src="require('@/assets/svg/foundation/bloom-partnership.svg')"
          class="m-auto w-200 mb-10"
        >
        <!-- eslint-enable vue/no-v-html -->
        <div class="flex mt-10">
          <button
            class="font-bold"
            @click="handleLeftClick"
          >
            {{ bottomLeftText }}
          </button>
          <button
            class="ml-auto font-bold"
            @click="handleRightClick"
          >
            {{ bottomRightText }}
          </button>
        </div>
      </div>
      <div
        v-for="(item, index) in featureList"
        v-else
        :key="index"
        class="plan-selection-card__feature-list--item"
      >
        <button
          :class="{'cursor-default': readOnly}"
          class="plan-selection-card__feature-list--question"
          @click="featureClick(index)"
        >
          <font-awesome-icon
            v-if="item.checked"
            :class="textColorClass()"
            icon="check"
          />
          <font-awesome-icon
            v-else
            class="plan-selection-card__xmark ml-5"
            icon="xmark"
          />
          <span
            :class="{
              'plan-selection-card__feature-list--unavailable': !item.checked,
              'font-normal': readOnly
            }"
          >
            {{ item.text }}
          </span>
          <font-awesome-icon
            v-if="!readOnly"
            class="opacity-50"
            icon="chevron-right"
          />
        </button>
      </div>
    </div>
    <div class="plan-selection-card__button-container">
      <AppButton
        v-if="!readOnly"
        class="plan-selection-card__button"
        :class="unfeaturedCardButton"
        :text="buttonText"
        :animated="false"
        :theme="theme"
        size="small"
        @click="handleSelectionClick(title, featuredCard)"
      />
    </div>
  </div>
</template>

<script>
import theme from '@/mixins/theme'
import AppButton from '@/components/common/buttons/AppButton'

export default {
  // Name
  name: 'PlanSelectionCard',

  // Components
  components: {
    AppButton
  },

  mixins: [theme],

  // Props
  props: {
    theme: {
      type: String,
      default: 'blue'
    },

    title: {
      type: String,
      default: ''
    },

    subtitle: {
      type: String,
      default: ''
    },

    basicCard: {
      type: Boolean,
      default: false
    },

    features: {
      type: Array,
      default: () => []
    },

    featuredCard: {
      type: Boolean,
      default: false
    },

    readOnly: {
      type: Boolean,
      default: false
    },

    foundationDeclined: {
      type: Boolean,
      default: false
    },

    isMobile: {
      type: Boolean,
      default: false
    },

    addBanner: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      featureIndex: 0,
      showFeatureInfo: false
    }
  },

  computed: {
    headerGradient () {
      return this.basicCard ? 'plan-selection-card__header--basic' : 'plan-selection-card__header--gold'
    },

    unfeaturedCardButton () {
      return !this.featuredCard ? 'plan-selection-card__button--unfeatured' : ''
    },

    buttonText () {
      return this.featuredCard ? 'Selected' : 'Select'
    },

    clickedItem () {
      return this.featureList[this.featureIndex]
    },

    bottomLeftText () {
      return this.featureIndex === 0 ? 'Collapse' : '< Back'
    },

    bottomRightText () {
      return this.featureIndex === this.featureList.length - 1 ? 'Collapse' : 'Next >'
    },

    isBloomFlow () {
      return this.$store.state.foundation.isBloomFlow
    },

    featureList () {
      if (this.isBloomFlow) return this.features

      return this.features.filter(item => !item.bloomOnly)
    },

    bannerURL () {
      return require('@/assets/svg/try-foundation-banner-v2.svg')
    }
  },

  methods: {

    handleCardClick (title) {
      if (this.isMobile) this.$emit('clicked', title)
    },

    featureClick (index) {
      if (this.readOnly || (!this.featuredCard && this.isMobile)) return
      this.showFeatureInfo = true
      this.featureIndex = index
    },

    handleRightClick () {
      if (this.featureIndex === this.featureList.length - 1) {
        this.showFeatureInfo = false
      } else {
        this.featureIndex++
      }
    },

    handleLeftClick () {
      if (this.featureIndex === 0) {
        this.showFeatureInfo = false
      } else {
        this.featureIndex--
      }
    },

    handleSelectionClick (title, featuredCard) {
      if (!featuredCard) this.$emit('clicked', title)
    }
  }
}
</script>

<style scoped lang="scss">
  .plan-selection-card {
    @apply h-auto shadow rounded-10 flex flex-col m-5 sm:m-10;

    &-decline {
      &__selected {
        box-shadow: 0 0 10px rgba(0, 157, 189, 0.75);

        @screen sm {
          box-shadow: 0 0 20px rgba(0, 157, 189, 0.75);
        }
      }
    }

    &-foundation {
      &__selected {
        box-shadow: 0 0 10px rgba(18, 41, 165, 0.75);

        @screen sm {
          box-shadow: 0 0 20px rgba(18, 41, 165, 0.75);
        }
      }
    }

    &__header {
      @apply relative flex items-start bg-gradient-to-r rounded-t-10 text-white py-15;

      &--text-container {
        @apply flex flex-col;
      }

      &--basic {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05)), linear-gradient(95.27deg, rgba(178, 179, 218, 0.88) 2.53%, rgba(119, 185, 186, 0.88) 96.62%);

        @apply justify-center p-30;
      }

      &--gold {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.03), rgba(0, 0, 0, 0.03)), linear-gradient(107.46deg, rgba(255, 215, 155, 0.88) 0%, rgba(241, 178, 142, 0.88) 14.16%, rgba(135, 173, 217, 0.88) 61.08%);

        @apply justify-center p-30;
      }
    }

    &__title {
      @apply w-[250px] sm:w-auto self-center md:self-start text-xl font-bold font-display;
    }

    &__subtitle {
      @apply text-base self-center font-semibold mb-5;

      &--right {
        @apply md:self-end;
      }
    }

    &__feature-display {
      @apply flex flex-1 flex-col mt-5;
    }

    &__feature-list {
      @apply text-left px-30 py-20 flex flex-col flex-1;

      &--header {
        @apply font-semibold font-display text-lg;
      }

      &--question {
        @apply w-full flex justify-between my-10 font-semibold;

        span {
          @apply text-left w-full px-20;
        }

        svg {
          @apply mt-5;
        }
      }

      &--answer {
        @apply pb-20 text-sm;
      }

      &--unavailable {
        @apply opacity-50;
      }

      &--show-banner {
        @apply pt-50;
      }
    }

    &__xmark {
      @apply opacity-50;
    }

    &__button-container {
      @apply items-end hidden sm:flex;
    }

    &__button {
      @apply h-45 mx-50 mb-30 mt-auto w-full;

      &--unfeatured {
        @apply opacity-30 bg-black;
      }
    }

    &__banner {
      @apply absolute bottom-[-25px] left-[-24px] z-1;
      @apply max-w-none bottom-[-55px];
    }

    &__badge {
      @apply absolute left-[-60px] w-[85px] top-[55px] z-[2];
      @apply xs-only:left-[-37px] xs-only:top-[55px] xs-only:w-[75px];
    }
  }
</style>
